import React from 'react';
import { fetchNavHTML, fetchShopCategories } from 'libs/server/nav';
import { fetchUser } from 'libs/server/users';
import { GetServerSideProps } from 'next';
import { badRequest } from 'libs/universal/errors';

import { getIsGuest } from 'libs/universal/utils/getIsGuest';

import { SecondaryNavType } from 'components/Nav/types';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import type { WDiscountTermsView, WStoreNavView } from '@zola/svc-web-api-ts-client';

import Nav from 'components/Nav';
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';
import H from '@zola/zola-ui/src/typography/Headings';
import P from '@zola/zola-ui/src/typography/Paragraphs';
import { Container, InnerContainer, TitleContainer } from 'components/Discount/Discount.styles';
import Head from 'next/head';
import Footer from 'components/ui/Footer/Footer';

import NAV_DATA from '@zola-helpers/client/dist/es/constants/navConstants';

import { PageWrapper } from 'components/LP/v3/PageWrapper.styles';
import { getDiscountById } from 'libs/server/discount';

type DiscountTermsPageProps = {
  navHTML: string | null;
  userCtx: UserContext | null;
  shopCategories: WStoreNavView;
  discount: WDiscountTermsView;
};

const DiscountTermsPage: React.FC<DiscountTermsPageProps> = (props): JSX.Element => {
  const { navHTML, userCtx, shopCategories, discount } = props;

  const isGuest = getIsGuest(userCtx);
  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);

  return (
    <>
      <Head>
        <title>{discount.label} - Zola</title>
        <meta name="description" content={discount.terms} />
        <meta property="og:title" content={discount.label} />
        <meta property="og:description" content={discount.terms} />
        <meta
          property="og:image"
          content="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/logos/zola-heart-marine.png"
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="zola.com" />
        <meta name="twitter:site" content="@zola" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`https://www.zola.com/discount-terms/${discount.id}`} />
      </Head>
      <Nav
        navHTML={navHTML}
        showPromo
        user={userCtx}
        disablePrimaryNavCollapse
        activePrimaryLink={NAV_DATA.PRIMARY.REGISTRY}
        shopCategories={shopCategories}
        secondaryNavType={SecondaryNavType.REGISTRY}
      />
      <PageWrapper isPostAuthNav3={isPostAuthNav3Enabled}>
        <Container isGuest={isGuest}>
          <InnerContainer>
            <TitleContainer>
              <H.TitleMedium1 presentation="h3">{discount.label}</H.TitleMedium1>
            </TitleContainer>
            <P.BodyBase>{discount.terms}</P.BodyBase>
          </InnerContainer>
        </Container>
        <Footer userCtx={userCtx} />
      </PageWrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<DiscountTermsPageProps> = async ({
  params,
  req,
}) => {
  if (!params || !params.id || Array.isArray(params.slug)) {
    throw badRequest('Missing or invalid discount id');
  }

  const [navHTML, userCtx, shopCategories, discount] = await Promise.all([
    fetchNavHTML(req),
    fetchUser(req),
    fetchShopCategories(),
    getDiscountById(Number(params.id)),
  ]);

  if (discount === null) {
    return { notFound: true };
  }

  return {
    props: {
      navHTML,
      userCtx,
      shopCategories,
      discount,
    },
  };
};

export default DiscountTermsPage;
